import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { View, ScrollView, StyleSheet, Animated } from "react-native";
import NtTabItem from "./NtTabItem";
import { useTheme } from "../context/ThemeContext";

/**
 * Tab strip with an animated sliding pill behind the selected tab.
 * Each tab measures itself via `onLayout`; when the selection changes,
 * the slider tweens its `translateX` and `width` to the new tab.
 */
function NtTabController({
  containerStyle,
  contentStyle,
  options,
  selection,
  onPress,
}) {
  const { theme } = useTheme();
  const styles = useMemo(() => makeStyles(theme), [theme]);

  const [tabLayouts, setTabLayouts] = useState({});
  const slideX = useRef(new Animated.Value(0)).current;
  const slideW = useRef(new Animated.Value(0)).current;
  const initialized = useRef(false);

  const selectedIndex = options.findIndex((o) => o?.id === selection?.id);

  useEffect(() => {
    if (selectedIndex < 0 || !tabLayouts[selectedIndex]) return;
    const { x, width } = tabLayouts[selectedIndex];
    if (!initialized.current) {
      // First render — snap immediately, no animation.
      slideX.setValue(x);
      slideW.setValue(width);
      initialized.current = true;
    } else {
      Animated.parallel([
        Animated.spring(slideX, {
          toValue: x,
          useNativeDriver: false,
          friction: 8,
          tension: 50,
        }),
        Animated.spring(slideW, {
          toValue: width,
          useNativeDriver: false,
          friction: 8,
          tension: 50,
        }),
      ]).start();
    }
  }, [selectedIndex, tabLayouts, slideX, slideW]);

  const handleTabLayout = useCallback((index) => (e) => {
    const { x, width } = e.nativeEvent.layout;
    setTabLayouts((prev) => ({ ...prev, [index]: { x, width } }));
  }, []);

  return (
    <View style={[styles.container, containerStyle]}>
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={[styles.scroll, contentStyle]}
      >
        <View style={styles.pillRow}>
          <Animated.View
            style={[
              styles.slider,
              { transform: [{ translateX: slideX }], width: slideW },
            ]}
          />
          {options.map((item, index) => (
            <View key={item?.id ?? index} onLayout={handleTabLayout(index)}>
              <NtTabItem
                item={item}
                flat
                selected={selection?.id === item?.id}
                onPress={() => onPress?.(item)}
              />
            </View>
          ))}
        </View>
      </ScrollView>
    </View>
  );
}

const makeStyles = (t) =>
  StyleSheet.create({
    container: { flexDirection: "row", alignItems: "center" },
    scroll: { flexGrow: 1 },
    // `position: relative` is critical on RN-Web — without it the absolute
    // slider positions against the viewport instead of this row.
    pillRow: {
      flexDirection: "row",
      alignItems: "center",
      gap: 4,
      position: "relative",
    },
    slider: {
      position: "absolute",
      top: 0,
      bottom: 0,
      backgroundColor: t.brand,
      borderRadius: t.radius,
    },
  });

export default NtTabController;
